import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Modal, Field } from '../ui/Modal';
import { Icon } from '../ui/Icon';
import { api, ApiError } from '../../lib/api';
import { toast } from '../../stores/toast.store';

const KINDS = [
  { v: 'FATTURA24',        l: 'Fattura24',         desc: 'Fatturazione elettronica SDI' },
  { v: 'SLACK',            l: 'Slack',             desc: 'Notifiche task, alert, mention' },
  { v: 'FIGMA',            l: 'Figma',             desc: 'Sync deliverable design' },
  { v: 'GITHUB',           l: 'GitHub',            desc: 'Sync commit/PR ai progetti' },
  { v: 'META_ADS',         l: 'Meta Ads',          desc: 'Insight campagne Marketing' },
  { v: 'GOOGLE_CALENDAR',  l: 'Google Calendar',   desc: 'Sync meeting e lezioni' },
  { v: 'ZOOM',             l: 'Zoom',              desc: 'Generazione link meeting' },
  { v: 'HARVEST',          l: 'Harvest',           desc: 'Timesheet e ore' },
  { v: 'OTHER',            l: 'Altro',             desc: 'Integrazione custom' },
] as const;

export function NewIntegrationModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const [kind, setKind] = useState<typeof KINDS[number]['v']>('SLACK');
  const [name, setName] = useState('');
  const [apiKey, setApiKey] = useState('');

  const create = useMutation({
    mutationFn: () => api.post('/settings/integrations', {
      kind, name: name || KINDS.find((k) => k.v === kind)?.l, apiKey: apiKey || undefined,
    }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['integrations'] });
      toast.ok('Integrazione connessa', name || KINDS.find((k) => k.v === kind)?.l);
      onClose();
    },
    onError: (err) => toast.alert('Errore connessione integrazione', err instanceof ApiError ? err.message : 'Riprova'),
  });

  return (
    <Modal
      title="Nuova integrazione"
      kicker="Impostazioni · Servizi collegati"
      onClose={onClose}
      size="lg"
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose}>Annulla</button>
          <div className="spacer" />
          <button className="btn btn-primary" onClick={() => create.mutate()} disabled={create.isPending}>
            Connetti <Icon name="link" size={12} />
          </button>
        </>
      }
    >
      <div className="form-section">
        <div className="form-section-hd">Scegli la piattaforma</div>
        <div className="grid grid-cols-3 gap-2">
          {KINDS.map((k) => (
            <button
              key={k.v}
              onClick={() => { setKind(k.v); if (!name) setName(k.l); }}
              className="text-left p-3 rounded-2 border-2 transition hover:bg-paper-2"
              style={{ borderColor: kind === k.v ? 'var(--accent)' : 'var(--line)', background: kind === k.v ? 'var(--paper-2)' : 'var(--surface)' }}>
              <div className="font-semibold text-[13px]">{k.l}</div>
              <div className="mute text-[12px] mt-0.5">{k.desc}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="form-section">
        <div className="form-section-hd">Configurazione</div>
        <div className="form-grid">
          <Field label="Nome visualizzato" span={12}>
            <input className="inp" placeholder={KINDS.find((k) => k.v === kind)?.l} value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="API key / Token" hint="Verrà cifrata server-side con AES-256-GCM" span={12}>
            <input className="inp mono" type="password" placeholder="••••••••••••" value={apiKey} onChange={(e) => setApiKey(e.target.value)} />
          </Field>
        </div>
        <div className="mt-3 text-[12px] mute flex items-center gap-1.5">
          <Icon name="lock" size={12} />
          Le credenziali sono cifrate e non recuperabili dopo il salvataggio.
        </div>
      </div>
    </Modal>
  );
}
